import type { NoteName } from '@tunasong/schemas'
import type { Chord, ChordType } from './chord-types.js'
import { ALL_CHORDS } from './all-chords.js'

/**
 * Create a regexp for the chords
 *
 * We reverse the Ids to match the special chords (e.g., Csus2) before the plain chord (C)
 * The regex also supports slash chords like Am/G or C/E
 */
const chordIds = Object.keys(ALL_CHORDS).reverse()

/**
 * Pattern for extended chord variants not in the library (e.g., m7b5, 7#9)
 * Matches: root + quality + extension + alterations + sus
 */
const extendedChordPattern = `[A-G][#b]?(?:m|M|maj|min|dim|aug)?(?:7|9|11|13)?(?:b5|#5|b9|#9|b11|#11|b13|#13)*(?:sus[24])?`

const chordRegexpStr = `(${chordIds.join('|')}|${extendedChordPattern})(\/[A-G][#b]?)?([\n\r\t ]+|$)`
export const chordsRegexp = new RegExp(chordRegexpStr)

export const parseChordString = (text: string) => {
  // Pattern for extended chords like F#m7b5, C7#9, Dm7b5/A
  // Only handles chords with explicit alterations (b5, #5, b9, #9, etc.)
  // Simpler chords are handled by the library lookup
  const extendedPattern =
    /^([A-G][#b]?)(m|min|dim|aug)?(7|9|11|13|maj7|maj9|add9|add11|6)([b#][5913]+)(sus[24])?(?:\s*\/\s*([A-G][#b]?))?$/
  const extendedMatch = text.match(extendedPattern)

  if (extendedMatch) {
    const variant = [
      extendedMatch[2] ?? '',
      extendedMatch[3] ?? '',
      extendedMatch[4] ?? '',
      extendedMatch[5] ?? '',
    ].join('') as ChordType
    return {
      root: extendedMatch[1] as NoteName,
      variant,
      bassNote: extendedMatch[6] as NoteName,
    } satisfies Chord
  }

  // Pattern for basic slash chords like Am/G, C/E, Cmaj7/E
  // Must have a slash to match here, otherwise fall through to library
  const slashPattern =
    /^([A-G][#b]?)(m|min|dim|aug|maj7|maj9|7|9|6|add9|sus2|sus4)?(?:\s*\/\s*([A-G][#b]?))$/
  const slashMatch = text.match(slashPattern)

  if (slashMatch) {
    return {
      root: slashMatch[1] as NoteName,
      variant: (slashMatch[2] ?? '') as ChordType,
      bassNote: slashMatch[3] as NoteName,
    } satisfies Chord
  }

  return undefined
}
